if (!customElements.get('interactive-world-map')) {
  customElements.define(
    'interactive-world-map',
    class InteractiveWorldMap extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('[data-map-viewport]');
        this.stage = this.querySelector('[data-map-stage]');
        this.template = this.querySelector('[data-world-template]');
        this.sheet = this.querySelector('[data-map-sheet]');
        this.sheetNext = this.querySelector('[data-sheet-next]');
        this.exploreLink = this.querySelector('[data-map-explore]');
        if (!this.viewport || !this.stage || !this.template) return;

        this.aspectRatio = Number(this.viewport.dataset.mapRatio) || 2;
        this.initialLongitude = Number(this.viewport.dataset.initialLongitude) || 0;
        this.longitudeOffset = Number(this.viewport.dataset.longitudeOffset) || 0;
        this.latitudeOffset = Number(this.viewport.dataset.latitudeOffset) || 0;
        this.offset = 0;
        this.pointerId = null;
        this.dragStartX = 0;
        this.dragStartOffset = 0;
        this.moved = false;
        this.suppressClickUntil = 0;

        this.renderWorldCopies();
        this.bindEvents();
        this.resizeObserver = new ResizeObserver(() => this.layout(true));
        this.resizeObserver.observe(this.viewport);
        this.layout(true);
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        window.clearTimeout(this.exploreCueTimeout);
      }

      renderWorldCopies() {
        this.stage.replaceChildren();
        for (let index = -1; index <= 1; index += 1) {
          const fragment = this.template.content.cloneNode(true);
          const copy = fragment.querySelector('[data-map-copy]');
          copy.dataset.copyIndex = String(index);
          copy.querySelectorAll('[data-map-location]').forEach((location) => {
            if (!this.positionLocation(location)) location.remove();
          });
          this.stage.append(fragment);
        }
        this.copies = [...this.stage.querySelectorAll('[data-map-copy]')];
      }

      positionLocation(location) {
        const parseCoordinate = (value) => {
          if (typeof value !== 'string' || value.trim() === '') return null;
          const parsed = Number(value);
          return Number.isFinite(parsed) ? parsed : null;
        };
        const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

        const exactX = parseCoordinate(location.dataset.mapX);
        const exactY = parseCoordinate(location.dataset.mapY);
        let x;
        let y;

        if (exactX !== null && exactY !== null) {
          x = clamp(exactX, 0, 100);
          y = clamp(exactY, 0, 100);
        } else {
          const latitude = parseCoordinate(location.dataset.latitude);
          const longitude = parseCoordinate(location.dataset.longitude);
          if (latitude === null || longitude === null) return false;

          const xAdjustment = parseCoordinate(location.dataset.xAdjustment) ?? 0;
          const yAdjustment = parseCoordinate(location.dataset.yAdjustment) ?? 0;
          const projectedX = ((longitude + 180) / 360) * 100 + this.longitudeOffset + xAdjustment;
          x = ((projectedX % 100) + 100) % 100;
          y = clamp(((90 - latitude) / 180) * 100 + this.latitudeOffset + yAdjustment, 4, 96);
        }

        location.style.setProperty('--g7-marker-x', `${x.toFixed(4)}%`);
        location.style.setProperty('--g7-marker-y', `${y.toFixed(4)}%`);
        return true;
      }

      bindEvents() {
        this.exploreLink?.addEventListener('click', (event) => {
          event.preventDefault();
          const visibleMarker = [...this.querySelectorAll('[data-map-marker]')].find((marker) => marker.tabIndex === 0);
          this.viewport.classList.remove('is-explore-cued');
          requestAnimationFrame(() => {
            this.viewport.classList.add('is-explore-cued');
            visibleMarker?.focus({ preventScroll: true });
          });
          window.clearTimeout(this.exploreCueTimeout);
          this.exploreCueTimeout = window.setTimeout(() => {
            this.viewport.classList.remove('is-explore-cued');
          }, 1000);
        });

        this.viewport.addEventListener('pointerdown', (event) => this.onPointerDown(event));
        this.viewport.addEventListener('pointermove', (event) => this.onPointerMove(event));
        this.viewport.addEventListener('pointerup', (event) => this.onPointerUp(event));
        this.viewport.addEventListener('pointercancel', (event) => this.onPointerUp(event));
        this.viewport.addEventListener('pointerout', (event) => {
          const suppressedLocation = event.target.closest('[data-map-location].is-hover-suppressed');
          if (!suppressedLocation) return;
          if (event.relatedTarget && suppressedLocation.contains(event.relatedTarget)) return;
          suppressedLocation.classList.remove('is-hover-suppressed');
        });

        this.viewport.addEventListener('click', (event) => {
          const closeButton = event.target.closest('[data-sheet-close]');
          if (closeButton) {
            this.closeSheet();
            return;
          }

          const nextButton = event.target.closest('[data-map-next], [data-sheet-next]');
          if (nextButton) {
            event.preventDefault();
            const currentLocation = nextButton.hasAttribute('data-sheet-next')
              ? this.activeSheetLocation
              : nextButton.closest('[data-map-location]');
            this.openNextLocation(currentLocation);
            return;
          }

          const marker = event.target.closest('[data-map-marker]');
          if (!marker) return;
          if (performance.now() < this.suppressClickUntil) {
            event.preventDefault();
            return;
          }

          const location = marker.closest('[data-map-location]');
          location.classList.remove('is-hover-suppressed');
          if (this.isMobile()) {
            this.openSheet(location);
          } else {
            this.positionPanel(location);
            const willOpen = !location.classList.contains('is-open');
            this.closeDesktopPanels();
            location.classList.toggle('is-open', willOpen);
            marker.setAttribute('aria-expanded', String(willOpen));
            location.querySelector('[data-map-panel]')?.setAttribute('aria-hidden', String(!willOpen));
          }
        });

        this.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') {
            this.closeSheet();
            this.closeDesktopPanels();
          }
        });

        document.addEventListener('click', (event) => {
          if (!this.contains(event.target)) this.closeDesktopPanels();
        });
      }

      layout(reset = false) {
        const width = this.viewport.clientWidth;
        const height = this.viewport.clientHeight;
        this.mapWidth = Math.max(width, height * this.aspectRatio);
        this.mapHeight = this.mapWidth / this.aspectRatio;

        this.copies.forEach((copy) => {
          const index = Number(copy.dataset.copyIndex);
          copy.style.width = `${this.mapWidth}px`;
          copy.style.height = `${this.mapHeight}px`;
          copy.style.left = `${index * this.mapWidth}px`;
        });

        if (reset) {
          const centerX = ((this.initialLongitude + 180) / 360) * this.mapWidth;
          this.offset = width / 2 - centerX;
        }
        this.wrapOffset();
        this.paint();
      }

      onPointerDown(event) {
        if (event.button !== 0 || event.target.closest('button, a')) return;
        this.pointerId = event.pointerId;
        this.dragStartX = event.clientX;
        this.dragStartOffset = this.offset;
        this.moved = false;
        this.viewport.setPointerCapture(event.pointerId);
        this.viewport.classList.add('is-dragging');
        this.closeDesktopPanels();
      }

      onPointerMove(event) {
        if (event.pointerId !== this.pointerId) return;
        const delta = event.clientX - this.dragStartX;
        if (Math.abs(delta) > 3) this.moved = true;
        this.offset = this.dragStartOffset + delta;
        this.wrapOffset();
        this.paint();
      }

      onPointerUp(event) {
        if (event.pointerId !== this.pointerId) return;
        this.viewport.releasePointerCapture?.(event.pointerId);
        this.pointerId = null;
        this.viewport.classList.remove('is-dragging');
        if (this.moved) {
          this.viewport.classList.add('has-moved');
          this.suppressClickUntil = performance.now() + 250;
        }
      }

      wrapOffset() {
        if (!this.mapWidth) return;
        while (this.offset <= -this.mapWidth / 2) this.offset += this.mapWidth;
        while (this.offset > this.mapWidth / 2) this.offset -= this.mapWidth;
      }

      paint() {
        this.stage.style.transform = `translate3d(${this.offset}px, 0, 0)`;
        requestAnimationFrame(() => this.updateFocusableMarkers());
      }

      updateFocusableMarkers() {
        const viewportRect = this.viewport.getBoundingClientRect();
        this.querySelectorAll('[data-map-marker]').forEach((marker) => {
          const rect = marker.getBoundingClientRect();
          const visible = rect.right > viewportRect.left && rect.left < viewportRect.right;
          marker.tabIndex = visible ? 0 : -1;
        });
        const visibleLocations = this.updateNextControls();
        if (!this.isMobile()) {
          visibleLocations.forEach((location) => this.positionPanel(location, viewportRect));
        }
      }

      getNavigableLocations() {
        const viewportRect = this.viewport.getBoundingClientRect();
        const seenKeys = new Set();
        return [...this.querySelectorAll('[data-map-location]')]
          .filter((location) => {
            const marker = location.querySelector('[data-map-marker]');
            if (!marker) return false;
            const rect = marker.getBoundingClientRect();
            const visible =
              rect.right > viewportRect.left &&
              rect.left < viewportRect.right &&
              rect.bottom > viewportRect.top &&
              rect.top < viewportRect.bottom;
            const key = location.dataset.locationKey;
            if (!visible || !key || seenKeys.has(key)) return false;
            seenKeys.add(key);
            return true;
          })
          .sort((first, second) => {
            const firstRect = first.querySelector('[data-map-marker]').getBoundingClientRect();
            const secondRect = second.querySelector('[data-map-marker]').getBoundingClientRect();
            return firstRect.left - secondRect.left || firstRect.top - secondRect.top;
          });
      }

      getNextLocation(currentLocation) {
        const locations = this.getNavigableLocations();
        if (locations.length < 2) return null;
        const currentKey = currentLocation?.dataset.locationKey;
        const currentIndex = locations.findIndex((location) => location.dataset.locationKey === currentKey);
        return locations[(currentIndex + 1 + locations.length) % locations.length];
      }

      getLocationTitle(location) {
        return location?.querySelector('[data-location-title]')?.textContent?.trim() || 'artwork location';
      }

      updateNextControls() {
        const locations = this.getNavigableLocations();
        locations.forEach((location, index) => {
          const button = location.querySelector('[data-map-next]');
          const nextLocation = locations.length > 1 ? locations[(index + 1) % locations.length] : null;
          if (!button) return;
          button.hidden = !nextLocation;
          if (nextLocation) button.setAttribute('aria-label', `View next location: ${this.getLocationTitle(nextLocation)}`);
        });
        return locations;
      }

      openNextLocation(currentLocation) {
        const nextLocation = this.getNextLocation(currentLocation);
        if (!nextLocation) return;

        if (this.isMobile()) {
          this.openSheet(nextLocation, { focusClose: false });
          this.sheetNext?.focus({ preventScroll: true });
          return;
        }

        currentLocation?.classList.add('is-hover-suppressed');
        nextLocation.classList.remove('is-hover-suppressed');
        this.closeDesktopPanels();
        this.positionPanel(nextLocation);
        nextLocation.classList.add('is-open');
        const marker = nextLocation.querySelector('[data-map-marker]');
        marker?.setAttribute('aria-expanded', 'true');
        nextLocation.querySelector('[data-map-panel]')?.setAttribute('aria-hidden', 'false');
        marker?.focus({ preventScroll: true });
      }

      positionPanel(location, viewportRect = this.viewport.getBoundingClientRect()) {
        const panel = location?.querySelector('[data-map-panel]');
        const marker = location?.querySelector('[data-map-marker]');
        if (!panel || !marker || !panel.offsetHeight) return;

        panel.classList.remove('is-below');
        panel.style.setProperty('--g7-panel-shift-x', '0px');

        const markerRect = marker.getBoundingClientRect();
        const gap = 12;
        const spaceAbove = markerRect.top - viewportRect.top - gap;
        const spaceBelow = viewportRect.bottom - markerRect.bottom - gap;
        if (panel.offsetHeight > spaceAbove && spaceBelow > spaceAbove) panel.classList.add('is-below');

        const panelRect = panel.getBoundingClientRect();
        const edgePadding = 16;
        let shiftX = 0;
        if (panelRect.left < viewportRect.left + edgePadding) {
          shiftX += viewportRect.left + edgePadding - panelRect.left;
        }
        if (panelRect.right + shiftX > viewportRect.right - edgePadding) {
          shiftX -= panelRect.right + shiftX - (viewportRect.right - edgePadding);
        }
        panel.style.setProperty('--g7-panel-shift-x', `${shiftX}px`);
      }

      isMobile() {
        return window.matchMedia('(max-width: 749px), (pointer: coarse)').matches;
      }

      openSheet(location, { focusClose = true } = {}) {
        if (!this.sheet || !location) return;
        const panel = location.querySelector('[data-map-panel]');
        const eyebrow = panel?.querySelector('[data-location-eyebrow]');
        const title = panel?.querySelector('[data-location-title]');
        const products = panel?.querySelector('[data-location-products]');
        this.sheet.querySelector('[data-sheet-eyebrow]').textContent = eyebrow?.textContent || '';
        this.sheet.querySelector('[data-sheet-title]').textContent = title?.textContent || '';
        this.sheet.querySelector('[data-sheet-products]').replaceChildren(...[...products.children].map((node) => node.cloneNode(true)));
        this.activeSheetLocation = location;
        const nextLocation = this.getNextLocation(location);
        this.sheetNext.hidden = !nextLocation;
        if (nextLocation) {
          this.sheetNext.setAttribute('aria-label', `View next location: ${this.getLocationTitle(nextLocation)}`);
        }
        this.sheet.hidden = false;
        requestAnimationFrame(() => this.sheet.setAttribute('data-open', 'true'));
        if (focusClose) this.sheet.querySelector('[data-sheet-close]')?.focus({ preventScroll: true });
      }

      closeSheet() {
        if (!this.sheet || this.sheet.hidden) return;
        this.sheet.setAttribute('data-open', 'false');
        window.setTimeout(() => {
          if (this.sheet.getAttribute('data-open') === 'false') {
            this.sheet.hidden = true;
            this.activeSheetLocation = null;
          }
        }, 230);
      }

      closeDesktopPanels() {
        this.querySelectorAll('.is-open').forEach((location) => {
          location.classList.remove('is-open');
          location.querySelector('[data-map-marker]')?.setAttribute('aria-expanded', 'false');
          location.querySelector('[data-map-panel]')?.setAttribute('aria-hidden', 'true');
        });
      }
    }
  );
}
